import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FormField } from "@/components/ui/FormField";
import { useChangePassword } from "@/features/auth/useAuth";
import { getApiErrorMessage } from "@/api/client";

const schema = z
  .object({
    currentPassword: z.string().min(1, "Enter your current password"),
    newPassword: z.string().min(8, "At least 8 characters").max(128),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match",
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    path: ["newPassword"],
    message: "New password must differ from the current one",
  });

type FormValues = z.infer<typeof schema>;

export function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const change = useChangePassword();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const close = () => {
    reset();
    change.reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Change password"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={change.isPending}
            onClick={handleSubmit(({ currentPassword, newPassword }) =>
              change.mutate({ currentPassword, newPassword }),
            )}
          >
            Update password
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit(({ currentPassword, newPassword }) =>
          change.mutate({ currentPassword, newPassword }),
        )}
      >
        <FormField label="Current password" htmlFor="currentPassword" error={errors.currentPassword?.message}>
          <Input
            id="currentPassword"
            type="password"
            autoComplete="current-password"
            error={!!errors.currentPassword}
            {...register("currentPassword")}
          />
        </FormField>
        <FormField label="New password" htmlFor="newPassword" error={errors.newPassword?.message}>
          <Input
            id="newPassword"
            type="password"
            autoComplete="new-password"
            error={!!errors.newPassword}
            {...register("newPassword")}
          />
        </FormField>
        <FormField label="Confirm new password" htmlFor="confirmPassword" error={errors.confirmPassword?.message}>
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            error={!!errors.confirmPassword}
            {...register("confirmPassword")}
          />
        </FormField>
        {change.isError && <p className="text-sm text-danger-500">{getApiErrorMessage(change.error)}</p>}
        <p className="text-xs text-ink-faint">You will be signed out of every device and asked to sign in again.</p>
        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
}
